import { P2GBox, P2GButton, P2GCard, P2GGrid, P2GProgress, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { usePlanCards } from './use-plan-cards';
import { formatSubscriptionPrice } from './pricing';
import { SubscriptionInterval, SubscriptionTier, TIER_ORDER, tierLabel } from './types';

interface props {
  /** Preferred interval to display a price for - falls back to whichever interval that tier has. */
  interval: SubscriptionInterval;
  onSelect: (tier: SubscriptionTier, interval: SubscriptionInterval) => void;
}

// Keyed off the interval the card ACTUALLY carries, not the one that was requested - the lookup
// below falls back to a tier's other interval, and an annual `unitAmount` is the full year's
// amount, so an unlabelled figure reads as a monthly price.
const INTERVAL_COPY: Record<SubscriptionInterval, { key: string; defaultValue: string }> = {
  month: { key: 'SUBSCRIBE_PLAN_PICKER_PER_MONTH', defaultValue: '/month' },
  year: { key: 'SUBSCRIBE_PLAN_PICKER_PER_YEAR', defaultValue: '/year' }
};

const PlanPicker = ({ interval, onSelect }: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { status, cards } = usePlanCards();

  if (status === 'loading') {
    return (
      <P2GBox display="flex" justifyContent="center" p={4}>
        <P2GProgress />
      </P2GBox>
    );
  }

  if (status === 'error' || cards.length === 0) {
    return (
      <P2GText variant="body2">
        {t('SUBSCRIBE_PLAN_PICKER_ERROR', { defaultValue: "We couldn't load plan pricing right now." })}
      </P2GText>
    );
  }

  return (
    <P2GGrid container spacing={2}>
      {TIER_ORDER.map((tier) => {
        const card =
          cards.find((c) => c.tier === tier && c.interval === interval) ?? cards.find((c) => c.tier === tier);
        if (!card) {
          return null;
        }
        return (
          <P2GGrid item xs={12} sm={6} key={tier}>
            <P2GCard variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <P2GText variant="subtitle1">{tierLabel(tier)}</P2GText>
              <P2GBox display="flex" alignItems="baseline" justifyContent="center" gap={0.5}>
                <P2GText variant="h5">{formatSubscriptionPrice(card.currency, card.priceMinorUnits)}</P2GText>
                <P2GText variant="body2">
                  {t(INTERVAL_COPY[card.interval].key, {
                    defaultValue: INTERVAL_COPY[card.interval].defaultValue
                  })}
                </P2GText>
              </P2GBox>
              <P2GButton variant="primary" fullWidth onClick={() => onSelect(tier, card.interval)} sx={{ mt: 2 }}>
                {t('SUBSCRIBE_PLAN_PICKER_CHOOSE', {
                  defaultValue: 'Choose {{tier}}',
                  tier: tierLabel(tier)
                })}
              </P2GButton>
            </P2GCard>
          </P2GGrid>
        );
      })}
    </P2GGrid>
  );
};

export default PlanPicker;
